import type { AppState } from '../../state/appState.js';
import { cloneNodes, cloneEdges } from '../documentClone.js';
import { notifyEditorTargetSynced } from '../editorEvents.js';
import { createUserLibraryEntityId } from '../../ids/entityIds.js';
import type {
  LibraryGeometryDef,
  LibraryMaterialDef,
} from '../../scene/layerState.js';
import type { EditorAction } from '../commandExecutor.js';
import type { EditorSessionState } from './session.js';
import {
  cloneLibraryItem,
  createForkedLibraryNameFor,
} from './shared.js';
import { captureEditorSessionFor } from './session.js';

export interface AutoForkBuiltInLibraryHooks {
  captureEditorSession?: (options?: { sync?: boolean }) => EditorSessionState;
  createUserLibraryId?: () => string;
  notifyEditorTargetSynced?: () => void;
}

export function autoForkEditedBuiltInLibraryIfNeededFor(
  appState: AppState,
  hooks: AutoForkBuiltInLibraryHooks = {},
): { replayActions: EditorAction[]; undoActions: EditorAction[] } | null {
  const captureSession = hooks.captureEditorSession ?? (() => captureEditorSessionFor(appState));
  const createLibraryId = hooks.createUserLibraryId ?? createUserLibraryEntityId;
  const notifySynced = hooks.notifyEditorTargetSynced ?? notifyEditorTargetSynced;
  const context = appState.editorContext;

  if (!context.libraryItemId) return null;

  if (context.mode === 'geometry-library' || context.mode === 'material-library') {
    const kind: 'geometry' | 'material' = context.mode === 'geometry-library' ? 'geometry' : 'material';
    const builtInStore = kind === 'geometry' ? appState.geometryLibrary.builtIn : appState.materialLibrary.builtIn;
    const userStore = kind === 'geometry' ? appState.geometryLibrary.user : appState.materialLibrary.user;
    const source = builtInStore[context.libraryItemId];
    if (!source) return null;

    const previousSession = captureSession({ sync: false });
    const extraFields = kind === 'geometry'
      ? { sizeMode: (source as LibraryGeometryDef).sizeMode }
      : { mode: (source as LibraryMaterialDef).mode };
    const item = {
      id: createLibraryId(),
      name: createForkedLibraryNameFor(appState, kind, source.name),
      nodes: cloneNodes(appState.nodes),
      edges: cloneEdges(appState.edges),
      builtIn: false,
      ...extraFields,
    };
    userStore[item.id] = item as typeof userStore[string];
    appState.editorContext = context.mode === 'geometry-library'
      ? { mode: 'geometry-library', libraryItemId: item.id }
      : { mode: 'material-library', libraryItemId: item.id };
    appState.editorGraphSlot = kind;
    appState.editorGraphDirty = false;
    notifySynced();

    const nextSession = captureSession();
    return {
      replayActions: [
        { type: 'library:restore', kind, item: cloneLibraryItem(item) },
        { type: 'editor:restoreSession', session: nextSession },
      ],
      undoActions: [
        { type: 'editor:restoreSession', session: previousSession },
        { type: 'library:delete', kind, id: item.id },
      ],
    };
  }

  return null;
}
